'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { FiUploadCloud, FiMic, FiLink, FiArrowUpRight, FiX, FiCheck, FiFileText } from 'react-icons/fi';
import { useLandingAccount, landingAccountType } from '../../lib/landingAccount';
import { getSiteCopy } from '../../lib/siteI18n';
import { sendLandingEvent } from '../../lib/analytics';
import { fetchGuestAvailability } from '../../lib/guestAvailability.mjs';
import './GuestEntry.css';
import LatestTranscript from './LatestTranscript';

const messages = {
  en: { fullPage: 'Full workspace', close: 'Back to the page', resume: 'Continue your transcript', studio: 'Your workspace', studioHint: 'Upload, record or import a link', loadingHint: 'Your file stays selected. One moment…', noCard: 'No signup. No credit card.', busy: 'An upload or recording is running. Finish or cancel it before leaving this workspace.', eyebrow: 'FROM AUDIO TO WORDS', title: 'Start with your recording.', drop: 'Drop your audio or video here', choose: 'Choose a file', record: 'Record audio', detail: 'MP3, MP4, WAV, M4A and more', limits: '3 free transcripts a day · Up to 35 min / 50 MB per file', guest: 'No account needed', benefit: 'Want to keep your history and use another device?', signup: 'Create a free account', loading: 'Opening your workspace…', fallback: 'Open the workspace in a full page', unavailable: 'Sign in to start. Guest access is being prepared.', retry: 'Try loading again', error: 'The workspace could not load. Your file has not been uploaded. Try again or open it in a full page.' },
  de: { fullPage: 'Vollständige App', close: 'Zurück zur Seite', resume: 'Transkript fortsetzen', studio: 'Dein Arbeitsbereich', studioHint: 'Datei hochladen, aufnehmen oder Link importieren', loadingHint: 'Deine Datei bleibt ausgewählt. Einen Moment…', noCard: 'Ohne Anmeldung. Ohne Kreditkarte.', busy: 'Ein Upload oder eine Aufnahme läuft. Beende den Vorgang, bevor du den Arbeitsbereich verlässt.', eyebrow: 'VON AUDIO ZU TEXT', title: 'Starte mit deiner Aufnahme.', drop: 'Audio oder Video hier ablegen', choose: 'Datei auswählen', record: 'Audio aufnehmen', detail: 'MP3, MP4, WAV, M4A und mehr', limits: '3 kostenlose Transkripte pro Tag · Bis 35 Min. / 50 MB pro Datei', guest: 'Keine Anmeldung nötig', benefit: 'Verlauf behalten und auf anderen Geräten nutzen?', signup: 'Kostenloses Konto erstellen', loading: 'Dein Arbeitsbereich wird geöffnet…', fallback: 'Arbeitsbereich als ganze Seite öffnen', unavailable: 'Melde dich zum Starten an. Der Gastzugang wird vorbereitet.', retry: 'Erneut laden', error: 'Der Arbeitsbereich konnte nicht geladen werden. Deine Datei wurde noch nicht hochgeladen. Versuche es erneut oder öffne die ganze Seite.' },
  fr: { fullPage: 'Espace complet', close: 'Retour à la page', resume: 'Continuer votre transcription', studio: 'Votre espace', studioHint: 'Importer un fichier, enregistrer ou coller un lien', loadingHint: 'Votre fichier reste sélectionné. Un instant…', noCard: 'Sans inscription. Sans carte bancaire.', busy: 'Un import ou un enregistrement est en cours. Terminez-le ou annulez-le avant de quitter cet espace.', eyebrow: 'DE L’AUDIO AU TEXTE', title: 'Commencez par votre audio.', drop: 'Déposez votre fichier audio ou vidéo ici', choose: 'Choisir un fichier', record: 'Enregistrer', detail: 'MP3, MP4, WAV, M4A et plus', limits: '3 transcriptions gratuites par jour · Jusqu’à 35 min / 50 Mo par fichier', guest: 'Sans inscription', benefit: 'Conserver votre historique et utiliser un autre appareil ?', signup: 'Créer un compte gratuit', loading: 'Ouverture de votre espace…', fallback: 'Ouvrir l’espace en pleine page', unavailable: 'Connectez-vous pour commencer. L’accès invité est en préparation.', retry: 'Réessayer', error: 'Impossible de charger l’espace. Votre fichier n’a pas été envoyé. Réessayez ou ouvrez la page complète.' },
};

const linkMessages = {
  en: { fileTab: 'Upload a file', urlTab: 'YouTube or URL', urlLabel: 'YouTube or media URL', urlPlaceholder: 'Paste a YouTube or media URL', importLink: 'Get transcript', urlHelp: 'Public media files, Drive, Dropbox or SoundCloud. YouTube: available captions.', youtubeGuide: 'YouTube transcript guide', invalidUrl: 'Paste a valid public http:// or https:// link.', transcribe: 'Transcribe this audio', urlLoading: 'Your link stays selected. One moment…' },
  de: { fileTab: 'Datei hochladen', urlTab: 'YouTube oder URL', urlLabel: 'YouTube- oder Medien-URL', urlPlaceholder: 'YouTube- oder Medien-URL einfügen', importLink: 'Transkript erstellen', urlHelp: 'Öffentliche Mediendateien, Drive, Dropbox oder SoundCloud. YouTube: verfügbare Untertitel.', youtubeGuide: 'Anleitung für YouTube-Transkripte', invalidUrl: 'Füge einen gültigen öffentlichen http://- oder https://-Link ein.', transcribe: 'Dieses Audio transkribieren', urlLoading: 'Dein Link bleibt ausgewählt. Einen Moment…' },
  fr: { fileTab: 'Importer un fichier', urlTab: 'YouTube ou URL', urlLabel: 'Lien YouTube ou média', urlPlaceholder: 'Collez un lien YouTube ou média', importLink: 'Créer la transcription', urlHelp: 'Fichiers média publics, Drive, Dropbox ou SoundCloud. YouTube : sous-titres disponibles.', youtubeGuide: 'Guide de transcription YouTube', invalidUrl: 'Collez un lien public http:// ou https:// valide.', transcribe: 'Transcrire cet audio', urlLoading: 'Votre lien reste sélectionné. Un instant…' },
};

const accessMessages = {
  en: { checking: 'Connecting… Your selection stays here.', unavailable: 'We cannot connect right now. Your selection stays here; try again in a moment.', retry: 'Try again' },
  de: { checking: 'Verbindung wird hergestellt… Deine Auswahl bleibt erhalten.', unavailable: 'Die Verbindung ist gerade nicht verfügbar. Deine Auswahl bleibt erhalten; versuche es gleich erneut.', retry: 'Erneut versuchen' },
  fr: { checking: 'Connexion en cours… Votre sélection est conservée.', unavailable: 'Connexion indisponible pour le moment. Votre sélection est conservée ; réessayez dans un instant.', retry: 'Réessayer' },
};

export default function GuestEntry({ locale = 'en', capturePageDrops = false, initialAction = 'upload', source = '', compact = false, filesProvider, ctaLabel, premiumOffer }) {
  const copy = { ...(messages[locale] || messages.en), ...(linkMessages[locale] || linkMessages.en) };
  const titleId = useId();
  const account = useLandingAccount();
  const accountRef = useRef(account);
  accountRef.current = account;
  const workspaceAccountType = useRef(null);
  const nav = getSiteCopy(locale).nav;
  const accessCopy = accessMessages[locale] || accessMessages.en;
  const registered = account?.kind === 'registered';
  const input = useRef(null);
  const dialog = useRef(null);
  const returnFocus = useRef(null);
  const closeHandler = useRef(null);
  const [mounted, setMounted] = useState(false);
  const frame = useRef(null);
  const frameUrl = useRef(null);
  const pending = useRef(null);
  const entry = useRef(null);
  const section = useRef(null);
  const viewed = useRef(false);
  const [availability, setAvailability] = useState('loading');
  const [configAttempt, setConfigAttempt] = useState(0);
  const waitingEntry = useRef(null);
  const [waitingForAccess, setWaitingForAccess] = useState(false);
  const enabled = availability === 'enabled' ? true : availability === 'disabled' ? false : null;
  const [open, setOpen] = useState(false);
  const openRef = useRef(false);
  openRef.current = open;
  const handoverRecovery = useRef(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  busyRef.current = busy;
  const [navigationNotice, setNavigationNotice] = useState(false);
  const [error, setError] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [handover, setHandover] = useState(0);
  const [activeAction, setActiveAction] = useState(initialAction === 'url' ? 'url' : 'upload');
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [entryHeight, setEntryHeight] = useState(0);
  const startHandler = useRef(null);
  const [pageDragging, setPageDragging] = useState(false);
  const dropAnywhere = { en: 'Drop your audio or video anywhere on this page', de: 'Audio oder Video überall auf dieser Seite ablegen', fr: 'Déposez votre audio ou vidéo n’importe où sur cette page' }[locale] || 'Drop your audio or video anywhere on this page';
  const hasFiles = event => Array.from(event.dataTransfer?.types || []).includes('Files');

  useEffect(() => {
    if (!capturePageDrops) return;
    let depth = 0;
    const reset = () => { depth = 0; setPageDragging(false); setDragging(false); };
    const enter = event => {
      if (!hasFiles(event)) return;
      event.preventDefault(); depth++; setPageDragging(true);
    };
    const over = event => {
      if (!hasFiles(event)) return;
      event.preventDefault(); event.dataTransfer.dropEffect = 'copy';
    };
    const leave = event => {
      if (!hasFiles(event)) return;
      if (--depth <= 0) reset();
    };
    const drop = event => {
      reset();
      if (!event.dataTransfer?.files.length || event.defaultPrevented) return;
      event.preventDefault();
      startHandler.current?.('upload', event.dataTransfer.files, 'drop');
    };
    const escape = event => { if (event.key === 'Escape') reset(); };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragover', over);
    window.addEventListener('dragleave', leave);
    window.addEventListener('drop', drop);
    window.addEventListener('dragend', reset);
    window.addEventListener('blur', reset);
    window.addEventListener('keydown', escape);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragover', over);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('drop', drop);
      window.removeEventListener('dragend', reset);
      window.removeEventListener('blur', reset);
      window.removeEventListener('keydown', escape);
    };
  }, [capturePageDrops]);

  const track = (name, params = {}) => sendLandingEvent(name, {
    landing_locale: locale, landing_path: window.location.pathname,
    guest_available: availability === 'loading' ? 'pending' : availability === 'temporarily_unavailable' ? 'unavailable' : availability,
    workspace_mode: 'embedded', entry_layout: 'focused_dialog', ...params,
    account_type: landingAccountType(accountRef.current) === 'registered' ? 'registered'
      : workspaceAccountType.current || landingAccountType(accountRef.current),
  });

  useEffect(() => {
    if (availability === 'loading' || viewed.current || !section.current || !window.IntersectionObserver) return;
    const observer = new IntersectionObserver(([item]) => {
      if (!item.isIntersecting || viewed.current) return;
      viewed.current = true;
      track('guest_entry_view');
      observer.disconnect();
    }, { threshold: 0.25 });
    observer.observe(section.current);
    return () => observer.disconnect();
    // Availability/locale are the only changing impression attributes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availability, locale]);

  useEffect(() => {
    const abort = new AbortController();
    fetchGuestAvailability(`${process.env.NEXT_PUBLIC_API_URL || 'https://api.instanttranscriber.com'}/guest/config`, {
      signal: abort.signal,
      onRetry: params => track('guest_config_retry', { operation: 'guest_config', ...params }),
    }).then(result => {
      if (abort.signal.aborted || result.status === 'cancelled') return;
      setAvailability(result.status);
      if (result.status === 'temporarily_unavailable') track('guest_config_error', { operation: 'guest_config', error_type: 'network', ...result, guest_available: 'unavailable' });
      else if (result.attempt_count > 1 || configAttempt > 0) track('guest_config_recovered', { operation: 'guest_config', guest_available: result.status, attempt_count: result.attempt_count });
    });
    return () => abort.abort();
    // Each explicit retry starts one bounded, abortable configuration check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configAttempt]);

  useEffect(() => {
    if (!waitingEntry.current || (!registered && enabled === null)) return;
    const next = waitingEntry.current;
    waitingEntry.current = null;
    setWaitingForAccess(false);
    startHandler.current?.(next.action, next.files, next.method, next.sourceUrl);
  }, [availability, registered, enabled]);

  useEffect(() => {
    if (!mounted) return;
    const send = () => {
      if (openRef.current && pending.current) frame.current?.contentWindow?.postMessage({ type: 'instanttranscriber:guest-entry', ...pending.current }, window.location.origin);
    };
    const receive = event => {
      if (event.origin !== window.location.origin || event.source !== frame.current?.contentWindow) return;
      if (['instanttranscriber:workspace-account', 'instanttranscriber:guest-entry-accepted'].includes(event.data?.type)
          && ['visitor', 'guest', 'registered'].includes(event.data.account_type)) {
        workspaceAccountType.current = event.data.account_type;
      }
      if (event.data?.type === 'instanttranscriber:workspace-ready') send();
      if (event.data?.type === 'instanttranscriber:workspace-close') closeHandler.current?.();
      if (event.data?.type === 'instanttranscriber:workspace-busy' && typeof event.data.busy === 'boolean') {
        setBusy(event.data.busy);
        if (!event.data.busy) setNavigationNotice(false);
      }
      if (event.data?.type === 'instanttranscriber:guest-entry-accepted' && pending.current && event.data.id === pending.current.id) {
        const loadMs = Math.max(0, Date.now() - pending.current.started);
        if (entry.current?.guest_entry_method === 'tool_result') track('tool_transcription_handover', {
          tool_id: source, source_intent: source, ...entry.current, elapsed_ms: loadMs });
        track('guest_workspace_ready', { ...entry.current, load_ms: loadMs, elapsed_ms: loadMs,
          event_source: openRef.current ? 'active_handover' : 'closed_handover' });
        if (handoverRecovery.current) track('guest_workspace_recovered', { ...entry.current,
          operation: 'workspace_handover', event_source: handoverRecovery.current, elapsed_ms: loadMs });
        handoverRecovery.current = null;
        pending.current = null; setReady(true); setError(false); clearInterval(retry); clearTimeout(timeout);
      }
    };
    window.addEventListener('message', receive);
    const activeId = pending.current?.id;
    const retry = open && activeId ? setInterval(send, 1000) : undefined;
    const timeout = open && activeId ? setTimeout(() => {
      if (!openRef.current || pending.current?.id !== activeId || busyRef.current) return;
      clearInterval(retry); setError(true);
      handoverRecovery.current = 'load_timeout';
      track('guest_workspace_error', { ...entry.current, operation: 'workspace_handover',
        error_type: 'load_timeout', elapsed_ms: Math.max(0, Date.now() - pending.current.started) });
    }, 45000) : undefined;
    send();
    return () => { window.removeEventListener('message', receive); clearInterval(retry); clearTimeout(timeout); };
    // Restart the handover timer only when the workspace is opened/retried.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, open, attempt, handover]);

  useEffect(() => {
    if (!busy) return undefined;
    const unload = (event) => { event.preventDefault(); event.returnValue = ''; };
    const navigateAway = (event) => {
      const anchor = event.target.closest?.('a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download') || anchor.getAttribute('href').startsWith('#')) return;
      event.preventDefault(); event.stopPropagation(); setNavigationNotice(true);
    };
    window.addEventListener('beforeunload', unload);
    document.addEventListener('click', navigateAway, true);
    return () => { window.removeEventListener('beforeunload', unload); document.removeEventListener('click', navigateAway, true); };
  }, [busy]);

  const close = () => {
    if (busy) { setNavigationNotice(true); track('guest_workspace_close_blocked'); return; }
    if (!openRef.current) return;
    track('guest_workspace_close', { ...entry.current, has_pending_action: Boolean(pending.current),
      event_source: pending.current ? 'closed_before_ready' : 'closed_after_ready' });
    if (pending.current) handoverRecovery.current ||= 'closed_before_ready';
    openRef.current = false;
    setError(false);
    setOpen(false);
  };
  closeHandler.current = close;

  useEffect(() => {
    if (!open || !dialog.current) return;
    const previousOverflow = document.body.style.overflow;
    const previousPadding = document.body.style.paddingRight;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
    if (!dialog.current.open) dialog.current.showModal();
    const element = dialog.current;
    return () => {
      element.close();
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPadding;
      returnFocus.current?.focus({ preventScroll: true });
    };
  }, [open]);

  const resume = () => {
    returnFocus.current = document.activeElement;
    track('guest_workspace_resume', entry.current || {});
    if (pending.current) pending.current.started = Date.now();
    setError(false);
    openRef.current = true;
    setOpen(true);
  };

  const start = (action, files = [], method = action === 'record' ? 'record_button' : 'file_picker', sourceUrl = '') => {
    const hasInput = files.length || (action === 'url' && sourceUrl);
    if (mounted && (!hasInput || busy || pending.current)) {
      resume();
      if (hasInput && (busy || pending.current)) setNavigationNotice(true);
      return;
    }
    if (!registered && enabled === null) {
      waitingEntry.current = { action, files: Array.from(files), method, sourceUrl };
      setWaitingForAccess(true);
      track('guest_entry_waiting', { guest_entry_action: action, guest_entry_method: method, file_count: files.length });
      return;
    }
    returnFocus.current = document.activeElement;
    if (!compact && !mounted) setEntryHeight(section.current?.getBoundingClientRect().height || 0);
    entry.current = { guest_entry_action: action, guest_entry_method: method };
    track('guest_entry_action', { ...entry.current, file_count: files.length });
    if (enabled === false && !registered) {
      track('guest_workspace_fallback', { ...entry.current, reason: 'guest_disabled' });
      window.location.assign(`/app/dashboard?lang=${locale}`); return;
    }
    track('guest_workspace_open', entry.current);
    if (!frameUrl.current) {
      const params = new URLSearchParams({ embedded: '1', studio: '1', lang: locale });
      if (action === 'url' || initialAction === 'url') params.set('intent', 'url_import');
      if (source) params.set('source_intent', source);
      frameUrl.current = `/app/dashboard?${params}`;
    }
    pending.current = { id: crypto.randomUUID(), action, method, files: Array.from(files), ...(sourceUrl ? { url: sourceUrl } : {}), started: Date.now() };
    handoverRecovery.current = null;
    openRef.current = true;
    setReady(false); setError(false); setMounted(true); setOpen(true);
    setHandover(n => n + 1);
  };
  const importLink = event => {
    event.preventDefault();
    try {
      const link = new URL(url.trim());
      if (!['http:', 'https:'].includes(link.protocol) || !link.hostname || link.username || link.password) throw new Error('invalid');
      setUrlError('');
      start('url', [], 'url_form', link.href);
    } catch { setUrlError(copy.invalidUrl); }
  };
  const chooseFile = () => {
    const files = filesProvider?.() || [];
    if (files.length) {
      track('tool_transcription_click', { tool_id: source, source_intent: source, file_count: files.length });
      start('upload', files, 'tool_result'); return;
    }
    if (mounted) { resume(); return; }
    track('guest_file_picker_open'); input.current?.click();
  };
  startHandler.current = start;
  const urlFirst = initialAction === 'url';
  const urlForm = <form className={`guest-entry__url${urlFirst ? '' : ' guest-entry__url--inline'}`} onSubmit={importLink}>
    <label htmlFor={`${titleId}-url`}>{copy.urlLabel}</label>
    <div className="guest-entry__url-controls">
      <input id={`${titleId}-url`} type="url" inputMode="url" autoComplete="off" required value={url} placeholder={copy.urlPlaceholder} onChange={event => { setUrl(event.target.value); setUrlError(''); }} aria-describedby={`${titleId}-help`} aria-invalid={Boolean(urlError)} />
      <button type="submit" className="guest-entry__choose" aria-label={copy.importLink} title={copy.importLink}><span>{copy.importLink}</span> <FiArrowUpRight aria-hidden="true" /></button>
    </div>
    <small id={`${titleId}-help`}>{copy.urlHelp}{urlFirst && <> <a href="/transcribe/youtube-to-text">{copy.youtubeGuide}</a></>}</small>
    {urlError && <p className="guest-entry__url-error" role="alert">{urlError}</p>}
  </form>;
  return <section ref={section} id={compact ? undefined : 'try-transcription'} className={`guest-entry${compact ? ' guest-entry--compact' : ''}`} style={entryHeight ? { minHeight: entryHeight } : undefined} aria-label={copy.title}>
    {pageDragging && <div className="guest-entry__page-drop" role="status"><FiUploadCloud aria-hidden="true" /><strong>{dropAnywhere}</strong><span>{copy.detail}</span></div>}
    {!compact && <LatestTranscript locale={locale} />}
    {!compact && <div className="guest-entry__intro">
      <span className="guest-entry__badge"><FiCheck aria-hidden="true" /> {registered ? nav.signedIn : copy.noCard}</span>
      <h2>{copy.title}</h2>
    </div>}
    {!compact && urlFirst && <div className="guest-entry__tabs" role="group" aria-label={copy.title}>
      <button type="button" aria-pressed={activeAction === 'upload'} onClick={() => setActiveAction('upload')}><FiUploadCloud aria-hidden="true" /> {copy.fileTab}</button>
      <button type="button" aria-pressed={activeAction === 'url'} onClick={() => setActiveAction('url')}><FiLink aria-hidden="true" /> {copy.urlTab}</button>
    </div>}
    {compact ? <button type="button" className="guest-entry__choose" onClick={chooseFile}><FiUploadCloud aria-hidden="true" /> {ctaLabel || copy.transcribe}</button> : activeAction === 'url' ? urlForm : <div className={`guest-entry__drop${dragging ? ' is-dragging' : ''}`}
      onDragOver={e => { if (hasFiles(e)) { e.preventDefault(); setDragging(true); } }} onDragLeave={() => setDragging(false)}
      onDrop={e => { if (!e.dataTransfer.files.length) return; e.preventDefault(); setDragging(false); start('upload', e.dataTransfer.files, 'drop'); }}>
      <span className="guest-entry__upload-icon"><FiUploadCloud size={30} aria-hidden="true" /></span>
      <p>{mounted ? copy.studio : copy.drop}</p>
      <button type="button" data-guest-file-picker className="guest-entry__choose" onClick={chooseFile}>{mounted ? copy.resume : copy.choose} <FiArrowUpRight aria-hidden="true" /></button>
      <small>{copy.detail}</small>
    </div>}
    <input ref={input} type="file" accept="audio/*,video/*,.m4a,.mp3,.wav,.mp4,.webm,.ogg,.flac,.mov,.mkv" multiple hidden onChange={e => { if (e.target.files.length) start('upload', e.target.files); e.target.value = ''; }} />
    {!compact && !mounted && <button type="button" className="guest-entry__record" onClick={() => start('record')}><FiMic aria-hidden="true" /> {copy.record}</button>}
    {!compact && !urlFirst && urlForm}
    {!compact && premiumOffer}
    {!compact && !registered && <p className="guest-entry__limits">{copy.limits}</p>}
    {!compact && <p className="guest-entry__status" aria-live="polite">{registered ? nav.savedToAccount : enabled === false ? copy.unavailable : copy.guest}</p>}
    {!registered && (waitingForAccess || availability === 'temporarily_unavailable') && <div className="guest-entry__availability" role="status">
      <p>{availability === 'temporarily_unavailable' ? accessCopy.unavailable : accessCopy.checking}</p>
      {availability === 'temporarily_unavailable' && <button type="button" className="guest-entry__record" onClick={() => {
        setAvailability('loading'); setConfigAttempt(value => value + 1);
      }}>{accessCopy.retry}</button>}
    </div>}
    {!compact && <div className="guest-entry__account">{!registered && copy.benefit}{' '}
      <a href={`/app/${registered ? 'dashboard' : 'signup'}?lang=${locale}`}
        data-analytics-cta-id={registered ? 'guest_entry_my_transcripts' : account ? 'guest_entry_complete_signup' : 'guest_entry_signup'} data-analytics-cta-location="guest_entry">
        {registered ? nav.workspace : account ? nav.completeSignup : copy.signup} <FiArrowUpRight aria-hidden="true" />
      </a>
    </div>}
    <dialog ref={dialog} className="guest-studio" aria-labelledby={`${titleId}-title`}
      onCancel={e => { e.preventDefault(); close(); }}>
      <div className="guest-studio__header">
        <div className="guest-studio__identity"><span className="guest-studio__mark"><FiFileText aria-hidden="true" /></span><div><h2 id={`${titleId}-title`}>{copy.studio}</h2><p>{copy.studioHint}</p></div></div>
        <a className="guest-studio__full" href={`/app/dashboard?lang=${locale}`} target="_top"
          data-analytics-cta-id="guest_full_workspace" data-analytics-cta-location="guest_dialog">{copy.fullPage} <FiArrowUpRight aria-hidden="true" /></a>
        <button type="button" className="guest-studio__close" autoFocus onClick={close} aria-label={copy.close}><FiX aria-hidden="true" /></button>
      </div>
      {navigationNotice && <p className="guest-entry__error" role="alert">{copy.busy}</p>}
      <div className="guest-studio__body" aria-busy={!ready}>
        {!ready && <div className="guest-studio__loading" role="status">
          <span className="guest-studio__pulse" aria-hidden="true" />
          <strong>{copy.loading}</strong><p>{entry.current?.guest_entry_action === 'url' ? copy.urlLoading : copy.loadingHint}</p>
          {error && <div className="guest-entry__error" role="alert">{copy.error}
            <button type="button" disabled={busy} onClick={() => {
              if (busyRef.current) return;
              track('guest_workspace_retry', entry.current);
              if (pending.current) pending.current.started = Date.now();
              setError(false); setAttempt(n => n + 1);
            }}>{copy.retry}</button>
            <a href={`/app/dashboard?lang=${locale}`} target="_top">{copy.fallback} ↗</a>
          </div>}
        </div>}
        {mounted && <iframe key={attempt} ref={frame} title="InstantTranscriber workspace"
          src={frameUrl.current} allow="microphone; display-capture" />}
      </div>
    </dialog>
  </section>;
}
